import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonRemoveAlt1Icon from '@mui/icons-material/PersonRemoveAlt1';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmActionModal from 'components/shared/modals/ConfirmActionModal';
import LoadingBlock from 'components/shared/blocks/LoadingBlock';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import OrgManagerCreateModal from 'components/org-manager-admin/OrgManagerCreateModal';
import uiSlice from 'store/modules/ui';
import logger from 'services/logger';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import { createSnack } from 'common/utils/general';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { ORGANIZATION_MANAGERS, SET_ORGANIZATION_MANAGERS } from 'api/graphql/orgManager';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Estado de membresía del gestor. Es informativo: el panel de bienestar NO exige plan activo,
// por eso el chip nunca usa el rojo de bloqueo.
const STATUS_TOKENS = {
  ACTIVE: { labelKey: 'managerStatusActive', fallback: 'Con plan', token: 'sessionStatus.completed' },
  IN_ARREARS: { labelKey: 'managerStatusArrears', fallback: 'En mora', token: 'sessionStatus.pending' },
  INACTIVE: { labelKey: 'managerStatusNoPlan', fallback: 'Sin plan', token: null }
};

const initialsOf = (name) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const formatDate = (iso) => {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

function StatTile({ value, label }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 1.5,
        borderRadius: 2.5,
        backgroundColor: 'surface.content',
        border: '1px solid',
        borderColor: 'hairline.main'
      }}>
      <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  );
}

StatTile.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired
};

function ManagerRow({ manager, onRemove, t }) {
  const status = STATUS_TOKENS[manager.membership_status] || STATUS_TOKENS.INACTIVE;
  const joinedAt = formatDate(manager.organization_joined_at);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 1.25, sm: 2 }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      sx={{
        px: { xs: 1.75, sm: 2 },
        py: 1.5,
        borderRadius: 2.5,
        backgroundColor: 'surface.content',
        border: '1px solid',
        borderColor: 'hairline.main',
        transition: `transform 200ms ${EASE}, border-color 200ms ${EASE}`,
        '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' }
      }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
        <Avatar
          src={manager.photo || undefined}
          sx={{
            width: 40,
            height: 40,
            fontSize: '0.8rem',
            fontWeight: 800,
            backgroundColor: 'surface.raised',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'hairline.main'
          }}>
          {initialsOf(manager.full_name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
              {manager.full_name}
            </Typography>
            {manager.is_online && (
              <Tooltip title={t('managerOnline', { defaultValue: 'En línea' })}>
                <Box
                  aria-label={t('managerOnline', { defaultValue: 'En línea' })}
                  sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'gold.main', flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
            {manager.email}
          </Typography>
        </Box>
      </Stack>

      {joinedAt && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}>
          {t('managerJoinedAt', { defaultValue: 'Vinculado el {{date}}', date: joinedAt })}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <Tooltip title={t('managerStatusHint', { defaultValue: 'El panel de bienestar no exige plan activo.' })}>
          <Chip
            size="small"
            label={t(status.labelKey, { defaultValue: status.fallback })}
            sx={{
              fontWeight: 700,
              backgroundColor: status.token ? `${status.token}.bg` : 'surface.raised',
              color: status.token ? `${status.token}.fg` : 'text.secondary'
            }}
          />
        </Tooltip>
        <Tooltip title={t('managerRemove', { defaultValue: 'Quitar el rol de gestor' })}>
          <IconButton
            size="small"
            aria-label={t('managerRemove', { defaultValue: 'Quitar el rol de gestor' })}
            onClick={() => onRemove(manager)}
            sx={{
              color: 'text.secondary',
              transition: `transform 200ms ${EASE}, color 200ms ${EASE}`,
              '&:hover': { color: 'gold.main' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            <PersonRemoveAlt1Icon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

ManagerRow.propTypes = {
  manager: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

/**
 * Pantalla admin "Prometeo": quién ve el CRM de cada empresa. Los gestores se agrupan por
 * organización porque la pregunta del admin siempre es "quién administra a X", no "quiénes son
 * todos los gestores".
 */
function OrgManagerList() {
  const { t } = useTranslation(['organizations', 'buttons']);
  const dispatch = useDispatch();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // El buscador consulta al servidor: se espera a que el admin deje de escribir.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, loading, error, refetch } = useQuery(ORGANIZATION_MANAGERS, {
    variables: { search: search || null },
    fetchPolicy: 'cache-and-network'
  });
  const [setManagers, { loading: removing }] = useMutation(SET_ORGANIZATION_MANAGERS);

  const managers = useMemo(() => data?.organizationManagers ?? [], [data]);

  const groups = useMemo(() => {
    const byOrg = new Map();
    managers.forEach((manager) => {
      const org = manager.organization;
      const key = org?.id ?? 'none';
      if (!byOrg.has(key)) {
        byOrg.set(key, {
          id: key,
          name: org?.name || t('managerNoCompany', { defaultValue: 'Sin empresa' }),
          isActive: org?.is_active !== false,
          membersCount: org?.members_count ?? 0,
          managers: []
        });
      }
      byOrg.get(key).managers.push(manager);
    });
    return Array.from(byOrg.values()).sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [managers, t]);

  // Con un solo grupo el acordeón cerrado sería un clic de peaje: se abre solo.
  useEffect(() => {
    if (groups.length === 1) setExpandedId(groups[0].id);
  }, [groups]);

  const snack = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `org-managers-${variant}`, variant, message })));

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      await setManagers({
        variables: {
          organization_id: removeTarget.organization?.id,
          user_ids: [removeTarget.id],
          is_manager: false
        }
      });
      snack('success', t('managerRemoved', { defaultValue: 'Ya no es gestor. Conserva su cuenta de cliente.' }));
      setRemoveTarget(null);
      refetch();
    } catch (removeError) {
      logger.error('setOrganizationManagers (remove) failed', removeError, { userId: removeTarget.id });
      setRemoveTarget(null);
      snack('error', t('managerSaveError', { defaultValue: 'No se pudo guardar. Inténtalo de nuevo.' }));
    }
  };

  const isEmpty = !loading && groups.length === 0;
  const hasSearch = search !== '';

  return (
    <Box>
      <PremiumCard sx={{ mb: 2.5 }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            justifyContent="space-between">
            <Box sx={{ maxWidth: 560 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                {t('managersTitle', { defaultValue: 'Gestores de bienestar' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {t('managersIntro', {
                  defaultValue:
                    'Son los contactos de cada empresa. Al iniciar sesión entran a {{path}} y ven el índice de bienestar, la adopción y los reportes de su gente.',
                  path: routes.orgClientDashboard.path
                })}
              </Typography>
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ ...premiumButtonSx, py: 1.1, px: 2.5, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t('managerNew', { defaultValue: 'Nuevo gestor de bienestar' })}
            </Button>
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <StatTile value={managers.length} label={t('managersCount', { defaultValue: 'Gestores' })} />
            <StatTile value={groups.length} label={t('managersCompanies', { defaultValue: 'Empresas con gestor' })} />
          </Stack>

          <TextField
            size="small"
            fullWidth
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('managerSearchPlaceholder', { defaultValue: 'Buscar por persona, correo o empresa' })}
            InputProps={{
              inputProps: {
                'aria-label': t('managerSearchPlaceholder', { defaultValue: 'Buscar por persona, correo o empresa' })
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'surface.content',
                '& fieldset': { borderColor: 'hairline.strong' },
                '&.Mui-focused fieldset': { borderColor: 'gold.main' }
              }
            }}
          />
        </Stack>
      </PremiumCard>

      {error && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{ borderRadius: 2, mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {t('retry', { defaultValue: 'Reintentar' })}
            </Button>
          }>
          {t('managersLoadError', { defaultValue: 'No se pudo cargar la lista de gestores.' })}
        </Alert>
      )}

      {loading && managers.length === 0 && (
        <LoadingBlock
          isValid={false}
          status={requestStatus.loading}
          type="table"
          skeletonProps={{ rows: 3, height: 88 }}
        />
      )}

      {isEmpty && !error && (
        <PremiumCard innerSx={{ textAlign: 'center' }}>
          <Stack spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <BusinessIcon sx={{ fontSize: 40, color: 'gold.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {hasSearch
                ? t('managersNoMatch', { defaultValue: 'Ningún gestor coincide' })
                : t('managersEmptyTitle', { defaultValue: 'Todavía no hay gestores' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420 }}>
              {hasSearch
                ? t('managersNoMatchBody', { defaultValue: 'Prueba con otro nombre, correo o empresa.' })
                : t('managersEmptyBody', {
                    defaultValue:
                      'Crea el primero: designa a alguien que ya está en la empresa o dale de alta a la persona de RRHH.'
                  })}
            </Typography>
            {!hasSearch && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{ ...premiumButtonSx, py: 1.1, px: 2.5, mt: 0.5 }}>
                {t('managerNew', { defaultValue: 'Nuevo gestor de bienestar' })}
              </Button>
            )}
          </Stack>
        </PremiumCard>
      )}

      <Stack spacing={1.5}>
        {groups.map((group) => (
          <Accordion
            key={group.id}
            disableGutters
            elevation={0}
            expanded={expandedId === group.id}
            onChange={() => setExpandedId(expandedId === group.id ? null : group.id)}
            sx={{
              backgroundColor: 'transparent',
              backgroundImage: 'none',
              '&::before': { display: 'none' }
            }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
              aria-label={group.name}
              sx={{
                px: { xs: 1.75, sm: 2 },
                borderRadius: 3,
                backgroundColor: 'surface.raised',
                border: '1px solid',
                borderColor: 'hairline.main',
                transition: `border-color 200ms ${EASE}, background-color 200ms ${EASE}`,
                '&:hover': { borderColor: 'hairline.strong' },
                '&.Mui-focusVisible': { backgroundColor: 'surface.raised', outline: '2px solid var(--otx-focus-ring)' },
                '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.25, minWidth: 0 }
              }}>
              <BusinessIcon sx={{ fontSize: 20, color: 'gold.main', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 800, minWidth: 0 }} noWrap>
                {group.name}
              </Typography>
              <Chip
                size="small"
                label={group.managers.length}
                sx={{ fontWeight: 700, backgroundColor: 'surface.content', color: 'text.secondary' }}
              />
              {!group.isActive && (
                <Chip
                  size="small"
                  label={t('inactive', { defaultValue: 'Inactiva' })}
                  sx={{ fontWeight: 700, backgroundColor: 'surface.content', color: 'text.secondary' }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1.25, pb: 0 }}>
              <Stack spacing={1.25}>
                {group.managers.map((manager) => (
                  <ManagerRow key={manager.id} manager={manager} onRemove={setRemoveTarget} t={t} />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <OrgManagerCreateModal
        open={createOpen}
        onClose={(created) => {
          setCreateOpen(false);
          if (created) refetch();
        }}
      />

      <ConfirmActionModal
        open={Boolean(removeTarget)}
        loading={removing}
        title={t('managerRemoveTitle', { defaultValue: 'Quitar el rol de gestor' })}
        message={t('managerRemoveMessage', {
          defaultValue:
            '{{name}} dejará de ver el panel de bienestar de {{company}}. Su cuenta y sus datos siguen intactos.',
          name: removeTarget?.full_name || '',
          company: removeTarget?.organization?.name || ''
        })}
        confirmLabel={t('managerRemoveConfirm', { defaultValue: 'Quitar el rol' })}
        onConfirm={confirmRemove}
        onClose={() => setRemoveTarget(null)}
      />
    </Box>
  );
}

export default OrgManagerList;
